import { FormApi } from "final-form";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";

import { Heading } from "@bps/fluent-ui";
import {
  EncounterClinicalDataDto,
  MedicalHistoryClinicalDataItemDto
} from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { Permission } from "@libs/gateways/core/CoreGateway.dtos.ts";
import { usePatientRecordScreenContext } from "@modules/clinical/screens/context/PatientRecordScreenContext.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { RootStore } from "@stores/root/RootStore.ts";
import {
  DataFetcher,
  withFetch
} from "@ui-components/data-fetcher/DataFetcher.tsx";
import { SubmissionFormDialog } from "@ui-components/form/submission-form-dialog/SubmissionFormDialog.tsx";

import { ConditionContext } from "../context/ConditionContext.ts";
import {
  conditionModalFormNameOf,
  ConditionModalFormValues
} from "./Condition.types.ts";
import { ConditionModalFallback } from "./ConditionModalFallback.tsx";
import { ConditionModalFormFields } from "./ConditionModalFormFields.tsx";
import { ConditionModalFormValidator } from "./ConditionModalFormValidator.ts";

interface ConditionModalBaseProps {
  disabled?: boolean;
  onDismiss?: () => void;
  onSubmit?: (values: ConditionModalFormValues) => void;
  hideAddDiagnosisOption?: boolean;
}

const ConditionModalBase: React.FC<ConditionModalBaseProps> = observer(
  ({ disabled, onSubmit, onDismiss, hideAddDiagnosisOption }) => {
    const { core, booking } = useStores();
    const { clinicalRecord } = usePatientRecordScreenContext();
    const validator = new ConditionModalFormValidator(core);

    const {
      hiddenConditionModal,
      hideConditionModal,
      patientId,
      practice,
      submitConditionModal,
      handleSubmitSucceeded,
      initialValues,
      episodeOfCare
    } = useContext(ConditionContext);

    const medicalHistories =
      clinicalRecord?.clinicalData?.medicalHistory?.medicalHistories ?? [];

    const medicalHistory = medicalHistories.find(
      x => x.diagnosisCode?.originalText === initialValues.diagnosis
    );

    const [selectedDiagnosis, setSelectedDiagnosis] = useState<
      MedicalHistoryClinicalDataItemDto | undefined
    >(medicalHistory);

    const [deleteDiagnosis, setDeleteDiagnosis] = useState<boolean>(false);

    if (core.hasPermissions(Permission.AddDiagnosisAllowed)) {
      useEffect(() => {
        const handleHiddenConditionModal = async () => {
          if (selectedDiagnosis?.id) {
            const clinicalData: EncounterClinicalDataDto = {};
            const selectedDiagnosisText =
              selectedDiagnosis?.diagnosisCode?.originalText;

            const filteredRFV =
              clinicalRecord.clinicalData?.reasonForVisit?.reasonForVisits.filter(
                x => x.originalText !== selectedDiagnosisText
              );

            clinicalData.reasonForVisit = {
              eTag: clinicalRecord.clinicalData?.reasonForVisit?.eTag,
              reasonForVisits: filteredRFV || []
            };

            const filteredMDH =
              clinicalRecord?.clinicalData?.medicalHistory?.medicalHistories?.filter(
                x => x.diagnosisCode?.originalText !== selectedDiagnosisText
              ) || [];

            clinicalData.medicalHistory = {
              eTag: clinicalRecord.clinicalData?.medicalHistory?.eTag,
              medicalHistories: filteredMDH,
              noSignificantHistory:
                filteredMDH?.[0]?.clinicallySignificant || false
            };

            await clinicalRecord.saveClinicalData(clinicalData);

            setDeleteDiagnosis(false);
            setSelectedDiagnosis(undefined);
          }
        };

        if (hiddenConditionModal && deleteDiagnosis) {
          handleHiddenConditionModal();
        }
      }, [
        hiddenConditionModal,
        deleteDiagnosis,
        selectedDiagnosis,
        clinicalRecord
      ]);
    }

    if (hiddenConditionModal) {
      return null;
    }

    const extraPromptConditionOnSubmit = (
      form: FormApi<ConditionModalFormValues>
    ) => {
      if (episodeOfCare) {
        const privateState = form.getFieldState(
          conditionModalFormNameOf("private")
        );

        const referralNumberState = form.getFieldState(
          conditionModalFormNameOf("referralNumber")
        );

        return Promise.resolve(
          !!privateState?.dirty || !!referralNumberState?.dirty
        );
      }

      return Promise.resolve(false);
    };

    const getDialogContentProps = (form: FormApi<ConditionModalFormValues>) => {
      if (episodeOfCare) {
        const privateState = form.getFieldState(
          conditionModalFormNameOf("private")
        );

        const referralNumberFormValue = form.getState().values.referralNumber;
        const referralNumberChanged =
          referralNumberFormValue !== initialValues.referralNumber;

        if (!!privateState?.dirty) {
          return {
            dialogContentProps: {
              title: "Change condition type",
              subText: "Are you sure you want to change the condition type?"
            },
            confirmButtonProps: { text: "Change condition" }
          };
        } else if (referralNumberChanged) {
          return {
            dialogContentProps: {
              title: `Confirm changing claim to ${referralNumberFormValue}`,
              subText: `
                  Changing "${
                    initialValues.referralNumber ?? ""
                  }" to "${referralNumberFormValue}" will impact billing and claiming, we recommend this is ONLY done under ACC direction`
            },
            confirmButtonProps: { text: "Change claim number" }
          };
        }
      }

      return undefined;
    };

    const patientName: string | undefined =
      patientId && practice.contactsMap.get(patientId)?.name;

    const getData = async ({ practice }: RootStore) => {
      const [defaultInsurerId, claimEpisodesOfCare] = await Promise.all([
        practice.getDefaultInsurers().then(async defaultInsurer => {
          const defaultInsurerId = defaultInsurer.defaultNzPublicInsurerId;
          if (defaultInsurerId) {
            await practice.getContact(defaultInsurerId);
          }
          return defaultInsurerId;
        }),
        episodeOfCare?.loadClaimEpisodeOfCare({
          includeInactive: true
        }) // This API request might not be needed as the episodeOfCare should already have the claimEpisodeOfCare loaded
      ]);

      return {
        defaultInsurerId,
        claimEpisodesOfCare
      };
    };

    return (
      <DataFetcher fetch={getData} noExceptionsHandlers>
        {(data, loading, error) => (
          <SubmissionFormDialog
            dialogName="Condition setup"
            loadingData={loading}
            dataLoadingError={error?.message}
            initialValues={initialValues}
            Fallback={ConditionModalFallback}
            onSubmitSucceeded={values => {
              handleSubmitSucceeded(values);
              booking.ui.setSecondColumnContent(undefined);
              onSubmit && onSubmit(values);
            }}
            onSubmit={submitConditionModal}
            buttonsProps={form => {
              return {
                hideButtonsSeparator: true,
                disableSubmitOnFormInvalid: true,
                submitButtonProps: {
                  text: "Save",
                  iconProps: { iconName: undefined }
                },
                promptOnSubmit: true,
                disableSubmitOnPristine:
                  initialValues.private === undefined && !!episodeOfCare,
                extraPromptConditionOnSubmit,
                submitConfirmProps: {
                  styles: {
                    main: {
                      ".ms-Dialog-subText": { marginBottom: 40 }
                    }
                  },
                  ...getDialogContentProps(form)
                }
              };
            }}
            validate={validator.validate}
            dialogProps={{
              minWidth: 480,
              maxWidth: 480,
              onDismiss: () => {
                hideConditionModal();
                if (core.hasPermissions(Permission.AddDiagnosisAllowed)) {
                  if (
                    selectedDiagnosis?.id &&
                    !clinicalRecord.patientEpisodesOfCare.some(
                      x =>
                        x.primaryDiagnosis ===
                        selectedDiagnosis?.diagnosisCode?.originalText
                    )
                  ) {
                    setDeleteDiagnosis(true);
                  }
                }
                onDismiss && onDismiss();
              },
              dialogContentProps: {
                title: (
                  <Heading variant="modal-heading">
                    {episodeOfCare ? "Edit condition" : "Condition"} setup{" "}
                    {patientName && `for ${patientName}`}
                  </Heading>
                )
              }
            }}
          >
            {() => (
              <ConditionModalFormFields
                defaultNzPublicInsurerId={data?.defaultInsurerId}
                claimEpisodesOfCare={data?.claimEpisodesOfCare}
                disabled={disabled ?? episodeOfCare?.claim?.isViewMode}
                selectedDiagnosis={
                  medicalHistory !== undefined
                    ? medicalHistory
                    : selectedDiagnosis
                }
                setSelectedDiagnosis={setSelectedDiagnosis}
                hideAddDiagnosisOption={hideAddDiagnosisOption}
              />
            )}
          </SubmissionFormDialog>
        )}
      </DataFetcher>
    );
  }
);

export const ConditionModal = withFetch(
  x => [x.practice.ref.sexes.load(), x.acc.ref.claimStatuses.load()],
  ConditionModalBase
);
